import { WarningAmberOutlined } from "@mui/icons-material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type CSSProperties, type FC, Fragment, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../components/InHouseTooltip/InHouseTooltip";
import { type DropdownItem, SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import SystemDialog from "../../../../components/UIToolkit/Popups/Dialog";
import { SystemReactTinyPopover } from "../../../../components/UIToolkit/Popups/SystemReactTinyPopover";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useErrorSnackbar } from "../../../../hooks/useErrorSnackbar";
import { INK, NEUTRAL, STATUS } from "../../../../theme/tokens";
import { CredentialStatus, type LocationCredentialResponse, type NurseWithExternalData } from "../../../../utils/models";
import { CommonHeader } from "../util/constants";
import { useCredentialsMutationHooks, useCredentialsQueryHook } from "./credentialsHooks";
import { NewCredentialPopover } from "./NewCredentialPopover";
import { type CredentialSortKey, useSynergyCredentialsState } from "./useSynergyCredentialsState";

const gridCellStyle = {
	background: "transparent",
	border: `1px solid ${NEUTRAL[200]}`,
	fontWeight: 400,
	fontSize: "14px",
	lineHeight: "16px",
	padding: "6px 10px",
	color: INK.SECONDARY,
	height: 44,
	display: "flex",
	alignItems: "center",
	justifyContent: "start",
	gap: 1,
} as const satisfies CSSProperties;

const spanStyle = {
	maxWidth: "100%",
	overflow: "hidden",
	textOverflow: "ellipsis",
	whiteSpace: "nowrap",
} as const satisfies CSSProperties;

const stickyHeaderStyle = {
	border: `1px solid ${NEUTRAL[200]}`,
	fontWeight: 600,
	fontSize: "14px",
	lineHeight: "100%",
	padding: "6px 10px",
	color: INK.SECONDARY,
	position: "sticky",
	top: 0,
	zIndex: 2,
	background: "var(--inhouse-primary-alpha-16)", // MUST have bg for sticky; tints with the user's chosen primary color
	display: "flex",
	alignItems: "center",
	gap: 1,
} as const satisfies CSSProperties;

const buttonHeaderStyle = {
	...stickyHeaderStyle,
	cursor: "pointer",
	justifyContent: "space-between",
} as const satisfies CSSProperties;

type Props = {
	nurse: NurseWithExternalData;
};

const SortButton = ({
	buttonKey,
	currentSortingKey,
	direction,
	onClick,
}: {
	buttonKey: CredentialSortKey;
	currentSortingKey: CredentialSortKey;
	direction: "asc" | "desc";
	onClick: (newSortingKey: CredentialSortKey, direction: "asc" | "desc") => void;
}) => {
	return (
		<IconButton
			onClick={() => onClick(buttonKey, buttonKey !== currentSortingKey ? "desc" : direction === "asc" ? "desc" : "asc")}
			size="small"
			sx={{
				opacity: currentSortingKey === buttonKey ? 1 : 0.3,
				transition: "opacity 0.3s ease",
			}}
		>
			{direction === "desc" || buttonKey !== currentSortingKey ? <ArrowDownwardIcon fontSize="inherit" /> : <ArrowUpwardIcon fontSize="inherit" />}
		</IconButton>
	);
};

export const SynergyCredentials: FC<Props> = ({ nurse }) => {
	const showErrorSnackbar = useErrorSnackbar();
	const { t } = useTranslation();

	const {
		isPopoverOpen,
		handleTogglePopover,
		selectedCredentialForDelete,
		showDeleteConfirmation,
		handleDeleteClick,
		handleDeleteCancel,
		handleDeleteConfirm,
		formattedLocationNameForRow,
		sortKey,
		sortDirection,
		handleSort,
	} = useSynergyCredentialsState();
	const { createCredential, isCreatingCredential, deleteCredential, isDeletingCredential } = useCredentialsMutationHooks();

	const { credentials, isLoading } = useCredentialsQueryHook({ nurseId: nurse.id, sortKey, sortDirection });

	const [editingCredentialId, setEditingCredentialId] = useState<string | null>(null);
	const [editedStatus, setEditedStatus] = useState<CredentialStatus | null>(null);

	const handleNewCredentialSubmit = useCallback(
		async (locationId: string, expiresAt: string | null, nurseIds: number[], status: CredentialStatus) => {
			if (!nurseIds || nurseIds.length === 0) {
				showErrorSnackbar("Please select at least one nurse for the credential.", {
					nurseIds,
				});
				return;
			}

			await createCredential({
				request: {
					nurse_ids: nurseIds,
					location_id: locationId,
					expires_at: expiresAt,
					status,
				},
				locationIdForInvalidation: locationId,
				nurseIdForInvalidation: nurse.id,
			});
			handleTogglePopover();
		},
		[createCredential, handleTogglePopover, showErrorSnackbar, nurse.id],
	);

	const handleDeleteConfirmClick = useCallback(async () => {
		if (selectedCredentialForDelete) {
			await deleteCredential({
				credentialId: selectedCredentialForDelete,
				nurseIdForInvalidation: nurse.id,
				locationIdForInvalidation: credentials.find((cred) => cred.id === selectedCredentialForDelete)?.department_location_id,
			});
			handleDeleteConfirm();
		}
	}, [selectedCredentialForDelete, deleteCredential, handleDeleteConfirm, nurse.id, credentials]);

	const handleEditClick = useCallback((credential: LocationCredentialResponse) => {
		setEditingCredentialId(credential.id);
		setEditedStatus(credential.status);
	}, []);

	const handleEditCancel = useCallback(() => {
		setEditingCredentialId(null);
		setEditedStatus(null);
	}, []);

	const handleEditSave = useCallback(
		async (credential: LocationCredentialResponse) => {
			if (!editedStatus) return;

			await createCredential({
				request: {
					nurse_ids: [credential.nurse_id],
					location_id: credential.department_location_id,
					expires_at: credential.expires_at?.toISO() ?? null,
					status: editedStatus,
				},
				locationIdForInvalidation: credential.department_location_id,
				nurseIdForInvalidation: nurse.id,
			});
			setEditingCredentialId(null);
			setEditedStatus(null);
		},
		[editedStatus, createCredential, nurse.id],
	);

	const isCredentialExpired = useCallback((credential: LocationCredentialResponse): boolean => {
		if (!credential.expires_at) return false;
		const expiresAt = credential.expires_at;
		const today = DateTime.now().startOf("day");
		return expiresAt < today;
	}, []);

	const formatDate = useCallback((date: DateTime): string => {
		return date.toFormat("MMM d, yyyy");
	}, []);

	const statusDropdownItems = useMemo<DropdownItem<CredentialStatus>[]>(() => {
		return Object.values(CredentialStatus).map((status) => ({
			value: status,
			label: status,
			id: status,
		}));
	}, []);

	return (
		<Box
			sx={{
				width: "100%",
				display: "flex",
				flexDirection: "column",
				gap: "16px",
			}}
		>
			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					width: "100%",
				}}
			>
				<CommonHeader>{t("credential.section.title", { defaultValue: "Synergy Credentials" })}</CommonHeader>
				<SystemReactTinyPopover
					isOpen={isPopoverOpen}
					positions={["bottom", "top"]}
					align="end"
					content={
						<NewCredentialPopover
							onSubmit={handleNewCredentialSubmit}
							onCancel={handleTogglePopover}
							isSubmitting={isCreatingCredential}
							defaultNurseId={nurse.id}
							nurseSelectionMode={"single"}
						/>
					}
				>
					<SystemButton buttonVariant="primary" onClick={handleTogglePopover} disabled={isLoading}>
						{t("credential.new.button", { defaultValue: "New" })}
					</SystemButton>
				</SystemReactTinyPopover>
			</Box>
			{isLoading ? (
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						padding: "20px",
					}}
				>
					<CircularProgress size={24} />
				</Box>
			) : credentials.length === 0 ? (
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						padding: "20px",
					}}
				>
					<Typography
						sx={{
							fontSize: "14px",
							color: INK.MUTED,
						}}
					>
						{t("credential.empty.message", { defaultValue: "No credentials found" })}
					</Typography>
				</Box>
			) : (
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "60px 2fr 1fr 1fr 300px 100px",
						width: "100%",
						maxHeight: "180px",
						overflow: "auto",
					}}
				>
					<Box sx={buttonHeaderStyle}>
						{t("ID", { defaultValue: "ID" })}
						<SortButton buttonKey="id" currentSortingKey={sortKey} direction={sortDirection} onClick={handleSort} />
					</Box>
					<Box sx={buttonHeaderStyle}>
						{t("credential.table.facility", { defaultValue: "Facility" })}
						<SortButton buttonKey="department_location_id" currentSortingKey={sortKey} direction={sortDirection} onClick={handleSort} />
					</Box>
					<Box sx={buttonHeaderStyle}>
						{t("credential.table.created", { defaultValue: "Created" })}
						<SortButton buttonKey="created_at" currentSortingKey={sortKey} direction={sortDirection} onClick={handleSort} />
					</Box>
					<Box sx={buttonHeaderStyle}>
						{t("credential.table.expires", { defaultValue: "Expires" })}
						<SortButton buttonKey="expires_at" currentSortingKey={sortKey} direction={sortDirection} onClick={handleSort} />
					</Box>
					<Box sx={buttonHeaderStyle}>
						{t("credential.table.status", { defaultValue: "Status" })}
						<SortButton buttonKey="status" currentSortingKey={sortKey} direction={sortDirection} onClick={handleSort} />
					</Box>
					<Box sx={stickyHeaderStyle}>{t("credential.table.actions", { defaultValue: "Actions" })}</Box>

					{credentials.map((credential) => {
						const isExpired = isCredentialExpired(credential);
						return (
							<Fragment key={credential.id}>
								<Box sx={gridCellStyle}>{credential.id.substring(credential.id.length - 4).toUpperCase()}</Box>
								<Box sx={gridCellStyle}>
									<span style={spanStyle}>{formattedLocationNameForRow(credential.department_location_id)}</span>
								</Box>
								<Box sx={gridCellStyle}>
									<span style={spanStyle}>{formatDate(credential.created_at)}</span>
								</Box>
								<Box sx={gridCellStyle}>
									<span style={spanStyle}>
										{credential.expires_at ? formatDate(credential.expires_at) : t("credential.no_expiration", { defaultValue: "No Expiration" })}
									</span>
									{isExpired && (
										<InHouseTooltip title={"Credential Expired"} placement={"top"}>
											<WarningAmberOutlined fontSize={"inherit"} sx={{ color: STATUS.ERROR.main }} />
										</InHouseTooltip>
									)}
								</Box>
								<Box sx={gridCellStyle}>
									{editingCredentialId === credential.id ? (
										<SystemDropdown
											multiple={false}
											items={statusDropdownItems}
											selectedItem={editedStatus}
											onChange={(value) => setEditedStatus(value || null)}
											valueToString={(value) => value || ""}
											label=""
											fullWidth={true}
										/>
									) : (
										<span style={spanStyle}>{credential.status}</span>
									)}
								</Box>
								<Box sx={gridCellStyle}>
									{editingCredentialId === credential.id ? (
										<Box
											sx={{
												display: "flex",
												gap: "4px",
											}}
										>
											<IconButton onClick={() => handleEditSave(credential)} size="small" disabled={isCreatingCredential}>
												{isCreatingCredential ? <CircularProgress size={14} /> : <CheckIcon fontSize="inherit" />}
											</IconButton>
											<IconButton onClick={handleEditCancel} size="small" disabled={isCreatingCredential}>
												<CloseIcon fontSize="inherit" />
											</IconButton>
										</Box>
									) : isDeletingCredential && selectedCredentialForDelete === credential.id ? (
										<CircularProgress size={gridCellStyle.fontSize} />
									) : (
										<Box
											sx={{
												display: "flex",
												gap: "4px",
											}}
										>
											<IconButton onClick={() => handleEditClick(credential)} size="small">
												<EditIcon fontSize="inherit" />
											</IconButton>
											<IconButton onClick={() => handleDeleteClick(credential.id)} size="small">
												<DeleteIcon fontSize="inherit" />
											</IconButton>
										</Box>
									)}
								</Box>
							</Fragment>
						);
					})}
				</Box>
			)}
			{showDeleteConfirmation && (
				<SystemDialog
					open={showDeleteConfirmation}
					onClose={handleDeleteCancel}
					title={t("credential.delete.title", { defaultValue: "Delete Credential?" })}
					body={t("credential.delete.message", { defaultValue: "Are you sure you want to delete this credential? This action cannot be undone." })}
					onConfirm={handleDeleteConfirmClick}
					cancel={t("common.cancel", { defaultValue: "Cancel" })}
					confirm={t("common.delete", { defaultValue: "Delete" })}
				/>
			)}
		</Box>
	);
};
